
import { chunk } from 'lodash'

export default {
  name: 'NativeSlider',
  props: {
    slideData: {
      type: Array,
      default: () => []
    },
    chunkCount: {
      type: Number,
      default: 0
    },
    spaceBetween: {
      type: Number,
      default: 0
    },
    slidesPerView: {
      type: Number,
      default: 1
    },
    slidesPerGroup: {
      type: Number,
      default: 1
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: false
    },
    lazyRange: {
      type: Number,
      default: 1
    },
    // 무한 루프(끝에서 처음으로, 처음에서 끝으로 순환). 앞뒤에 클론 슬라이드를 붙여 구현한다.
    loop: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentIndex: 0, // 논리적 인덱스(클론 제외, 외부에 노출되는 값)
      actualIndex: 0, // renderedSlides 상의 실제 인덱스(loop일 때 앞쪽 클론만큼 +1 오프셋)
      renderedSlides: [],
      isPointerDown: false,
      isDragging: false,
      dragMoved: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      translateX: 0,
      isTransitioning: false,
      isDestroyedLocal: false,
      resizeObserver: null,
      activeSlideResizeObserver: null
    }
  },
  computed: {
    chunkList () {
      if (this.slideData.length === 0) {
        return []
      }
      if (this.chunkCount > 0) {
        return chunk(this.slideData, this.chunkCount)
      }
      return this.slideData
    },
    totalSlides () {
      return this.snapIndexes.length
    },
    canSlide () {
      return this.totalSlides > 1
    },
    // 실제 DOM(renderedSlides) 상에서 현재 활성화된 인덱스. loop면 클론 오프셋이 반영된 actualIndex, 아니면 currentIndex.
    visibleIndex () {
      return this.loop ? this.actualIndex : this.currentIndex
    },
    isMultiView () {
      return !this.loop && this.chunkCount === 0 && this.slidesPerView > 1
    },
    snapIndexes () {
      if (this.chunkList.length === 0) {
        return []
      }
      if (!this.isMultiView) {
        return this.chunkList.map((_, index) => index)
      }

      const maxStartIndex = Math.max(0, this.chunkList.length - this.slidesPerView)
      const groupSize = Math.max(1, this.slidesPerGroup)
      const indexes = []

      for (let index = 0; index < maxStartIndex; index += groupSize) {
        indexes.push(index)
      }
      if (indexes[indexes.length - 1] !== maxStartIndex) {
        indexes.push(maxStartIndex)
      }

      return indexes
    },
    activeRenderIndex () {
      return this.isMultiView ? (this.snapIndexes[this.currentIndex] || 0) : this.visibleIndex
    },
    isBeginning () {
      return !this.loop && this.currentIndex <= 0
    },
    isEnd () {
      return !this.loop && this.currentIndex >= this.totalSlides - 1
    },
    wrapperStyle () {
      return {
        transform: `translateX(${this.translateX}px)`,
        transition: this.isTransitioning ? 'transform 0.3s ease-out' : 'none',
        gap: `${this.spaceBetween}px`
      }
    }
  },
  watch: {
    chunkList () {
      this.initSlides()
    }
  },
  mounted () {
    this.initSlides()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    this.isDestroyedLocal = true
    this.removeEventListeners()
    this.destroyActiveSlideResizeObserver()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initSlides () {
      this.$nextTick(() => {
        const items = this.chunkList

        if (!items || items.length === 0) {
          this.renderedSlides = []
          return
        }

        if (this.loop && items.length > 1) {
          // 앞뒤에 클론(마지막/첫 슬라이드)을 붙여, 경계에서 순간이동으로 무한루프를 흉내낸다
          this.renderedSlides = [
            { item: items[items.length - 1], _origIndex: items.length - 1, _cloneId: 'c-last' },
            ...items.map((it, i) => ({ item: it, _origIndex: i })),
            { item: items[0], _origIndex: 0, _cloneId: 'c-first' }
          ]
          this.actualIndex = 1
          this.currentIndex = 0
        } else {
          this.renderedSlides = items.map((it, i) => ({ item: it, _origIndex: i }))
          this.currentIndex = Math.min(this.currentIndex, this.totalSlides - 1)
          this.actualIndex = this.currentIndex
        }

        this.$nextTick(() => {
          this.translateX = this.loop
            ? -this.visibleIndex * this.getSlideStep()
            : this.getTranslateX(this.currentIndex)
          this.updateAutoHeight()
          // productList가 비동기로 채워지면 mounted 시점엔 컨테이너가 없어 리스너 등록이 no-op이 된다 — 여기서 재호출(재등록은 자동 무시돼 안전)
          this.setupEventListeners()
        })
      })
    },
    setupEventListeners () {
      const container = this.$refs.swiperContainer
      if (!container) { return }
      container.addEventListener('pointerdown', this.onPointerDown)
      container.addEventListener('pointermove', this.onPointerMove)
      container.addEventListener('pointerup', this.onPointerUp)
      container.addEventListener('pointercancel', this.onPointerUp)
    },
    removeEventListeners () {
      const container = this.$refs.swiperContainer
      if (!container) { return }
      container.removeEventListener('pointerdown', this.onPointerDown)
      container.removeEventListener('pointermove', this.onPointerMove)
      container.removeEventListener('pointerup', this.onPointerUp)
      container.removeEventListener('pointercancel', this.onPointerUp)
    },
    onPointerDown (e) {
      if (!this.canSlide) { return }
      // 마우스는 좌클릭만, 터치/펜은 그대로 허용
      if (e.pointerType === 'mouse' && e.button !== 0) { return }

      this.isPointerDown = true
      this.dragMoved = false
      this.pointerId = e.pointerId
      this.startDrag(e.clientX, e.clientY)
      // setPointerCapture는 드래그 확정 시점(onPointerMove)에 건다 — 여기서 걸면 단순 클릭도 캡쳐돼 안쪽 링크 클릭이 안 불린다
    },
    onPointerMove (e) {
      if (!this.isPointerDown || !this.isDragging) { return }
      if (this.pointerId !== null && e.pointerId !== this.pointerId) { return }

      const deltaX = e.clientX - this.startX
      const deltaY = e.clientY - this.startY

      if (!this.dragMoved) {
        // 이동량이 너무 작으면 방향을 아직 안 가른다 — 손떨림 수준에서 즉시 가르면 매번 드래그가 취소됐다
        if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) {
          return
        }
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          // 수직 의도가 더 크면 드래그를 취소하고 기본 스크롤을 허용한다
          this.isDragging = false
          return
        }
        this.dragMoved = true

        // 드래그 확정 시점에만 캡쳐를 걸어야 컨테이너 밖으로 나가도 계속 추적된다(클릭에는 안 건다)
        const container = this.$refs.swiperContainer
        if (container && container.setPointerCapture) {
          try {
            container.setPointerCapture(e.pointerId)
          } catch (err) {
            // pointer capture 실패는 드래그 동작에 치명적이지 않으므로 무시
          }
        }
      }

      e.preventDefault()
      this.currentX = e.clientX
      this.currentY = e.clientY
      this.updateDrag(e.clientX)
    },
    onPointerUp (e) {
      if (this.pointerId !== null && e.pointerId !== this.pointerId) { return }

      const container = this.$refs.swiperContainer
      if (container && container.releasePointerCapture) {
        try {
          container.releasePointerCapture(e.pointerId)
        } catch (err) {
          // 이미 해제된 capture는 무시
        }
      }

      this.isPointerDown = false
      this.pointerId = null

      if (this.dragMoved) {
        this.suppressNextClick()
      }

      this.endDrag()
    },
    suppressNextClick () {
      const container = this.$refs.swiperContainer
      if (!container) { return }
      const swallow = (evt) => {
        evt.preventDefault()
        evt.stopPropagation()
      }
      container.addEventListener('click', swallow, { capture: true, once: true })
      // 클릭이 발생하지 않는 케이스(예: 드래그 후 슬라이더 밖에서 포인터업)를 대비해 정리
      setTimeout(() => {
        container.removeEventListener('click', swallow, { capture: true })
      }, 0)
    },
    // 중첩 스와이프에서 자식→부모로 드래그를 넘길 때도 호출된다 — pointerdown이 없어도 isPointerDown을 켜야 이후 이벤트가 가드를 통과한다
    startDrag (x, y) {
      if (!this.canSlide) { return }
      this.isPointerDown = true
      this.isDragging = true
      this.dragMoved = false
      this.startX = x
      this.startY = y
      this.currentX = x
      this.currentY = y
    },
    updateDrag (x) {
      if (!this.isDragging || !this.canSlide) { return }

      const deltaX = x - this.startX
      const baseTranslateX = this.loop
        ? -this.visibleIndex * this.getSlideStep()
        : this.getTranslateX(this.currentIndex)

      if (!this.loop) {
        if (this.currentIndex === 0 && deltaX > 0) {
          this.translateX = baseTranslateX
          return
        }
        if (this.currentIndex === this.totalSlides - 1 && deltaX < 0) {
          this.translateX = baseTranslateX
          return
        }
      }

      this.translateX = baseTranslateX + deltaX
    },
    endDrag () {
      if (!this.canSlide) { return }
      if (!this.isDragging) { return }

      this.isDragging = false

      const deltaX = this.currentX - this.startX
      const slideStep = this.isMultiView ? this.getContainerWidth() : this.getSlideStep()
      const threshold = slideStep * 0.15

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          this.slidePrev()
        } else {
          this.slideNext()
        }
      } else if (this.loop) {
        this.slideToActualIndex(this.actualIndex)
      } else {
        this.slideToIndex(this.currentIndex)
      }
    },
    slideNext () {
      if (!this.canSlide) { return }

      if (this.loop) {
        this.actualIndex++
        this.currentIndex = (this.currentIndex + 1) % this.totalSlides
        this.slideToActualIndex(this.actualIndex)
        return
      }

      if (this.isEnd) {
        this.slideToIndex(this.currentIndex)
        return
      }
      this.slideToIndex(this.currentIndex + 1)
    },
    slidePrev () {
      if (!this.canSlide) { return }

      if (this.loop) {
        this.actualIndex--
        this.currentIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1
        this.slideToActualIndex(this.actualIndex)
        return
      }

      if (this.isBeginning) {
        this.slideToIndex(this.currentIndex)
        return
      }
      this.slideToIndex(this.currentIndex - 1)
    },
    slideToIndex (index) {
      if (this.loop) {
        const target = ((index % this.totalSlides) + this.totalSlides) % this.totalSlides
        this.currentIndex = target
        this.actualIndex = target + 1
        this.slideToActualIndex(this.actualIndex)
        return
      }

      const target = Math.max(0, Math.min(index, this.totalSlides - 1))
      const changed = target !== this.currentIndex

      this.currentIndex = target
      this.isTransitioning = true
      this.translateX = this.getTranslateX(this.currentIndex)

      const timeoutId = setTimeout(() => {
        if (!this.isDestroyedLocal) {
          this.isTransitioning = false
        }
      }, 300)
      this._transitionTimeout = timeoutId

      this.updateAutoHeight()

      if (changed) {
        this.$emit('changeIndex', this.currentIndex)
      }
    },
    // loop 전용: renderedSlides 상의 실제(클론 포함) 인덱스로 이동
    slideToActualIndex (actualIndex) {
      this.isTransitioning = true
      this.translateX = -actualIndex * this.getSlideStep()

      const timeoutId = setTimeout(() => {
        if (!this.isDestroyedLocal) {
          this.isTransitioning = false
          this.checkAndHandleInfiniteLoop()
        }
      }, 300)
      this._transitionTimeout = timeoutId

      this.updateAutoHeight()
      this.$emit('changeIndex', this.currentIndex)
    },
    // 클론 슬라이드에 도착했으면, 트랜지션 없이 대응하는 실제 슬라이드 위치로 순간이동한다
    checkAndHandleInfiniteLoop () {
      if (!this.loop || this.renderedSlides.length <= 1) { return }

      const len = this.renderedSlides.length
      let nextTranslateX = null

      if (this.actualIndex >= len - 1) {
        this.actualIndex = 1
        nextTranslateX = -this.actualIndex * this.getSlideStep()
      } else if (this.actualIndex <= 0) {
        this.actualIndex = len - 2
        nextTranslateX = -this.actualIndex * this.getSlideStep()
      }

      if (nextTranslateX !== null) {
        this.jumpToTranslateX(nextTranslateX)
      }
    },
    // 트랜지션 없이 위치를 순간이동시킨다. transition:none을 먼저 강제 커밋한 뒤 위치를 바꿔야
    // 잔여 트랜지션이 점프에 적용되어 화면이 되돌아가는 듯한 깜빡임이 생기지 않는다.
    jumpToTranslateX (value) {
      this.isTransitioning = false

      const wrapper = this.$refs.swiperWrapper
      if (wrapper) {
        wrapper.style.transition = 'none'
        // eslint-disable-next-line no-unused-expressions
        wrapper.offsetHeight // 강제 reflow로 transition:none을 확정 커밋
      }

      this.translateX = value
    },
    getContainerWidth () {
      const container = this.$refs.swiperContainer
      return container ? container.offsetWidth : 0
    },
    getSlideStep () {
      return this.getContainerWidth() + this.spaceBetween
    },
    getItemStep () {
      const visibleCount = Math.max(1, this.slidesPerView)
      const availableWidth = this.getContainerWidth() - (this.spaceBetween * (visibleCount - 1))
      return (availableWidth / visibleCount) + this.spaceBetween
    },
    getTranslateX (index) {
      if (!this.isMultiView) {
        return -index * this.getSlideStep()
      }
      return -(this.snapIndexes[index] || 0) * this.getItemStep()
    },
    getSlideStyle () {
      if (!this.isMultiView) {
        return { flex: '0 0 100%', width: '100%' }
      }

      const gapWidth = this.spaceBetween * (this.slidesPerView - 1)
      const width = `calc((100% - ${gapWidth}px) / ${this.slidesPerView})`
      return { flex: `0 0 ${width}`, width }
    },
    handleResize () {
      if (this.isDestroyedLocal) { return }
      this.translateX = this.loop
        ? -this.visibleIndex * this.getSlideStep()
        : this.getTranslateX(this.currentIndex)
      this.updateAutoHeight()
    },
    updateAutoHeight () {
      this.destroyActiveSlideResizeObserver()
      if (!this.autoHeight) { return }

      this.$nextTick(() => {
        const wrapper = this.$refs.swiperWrapper
        if (!wrapper) { return }
        const activeSlide = wrapper.children[this.activeRenderIndex]
        if (!activeSlide) { return }

        this.applyAutoHeight(activeSlide)

        if (typeof window !== 'undefined' && window.ResizeObserver) {
          this.activeSlideResizeObserver = new window.ResizeObserver(() => {
            this.applyAutoHeight(activeSlide)
          })
          this.activeSlideResizeObserver.observe(activeSlide)
        }
      })
    },
    applyAutoHeight (slideEl) {
      const wrapper = this.$refs.swiperWrapper
      if (!wrapper || !slideEl) { return }
      wrapper.style.height = `${slideEl.offsetHeight}px`
    },
    destroyActiveSlideResizeObserver () {
      if (this.activeSlideResizeObserver) {
        this.activeSlideResizeObserver.disconnect()
        this.activeSlideResizeObserver = null
      }
    },
    isSlideVisible (idx) {
      if (!this.loop) {
        if (this.isMultiView) {
          const startIndex = this.activeRenderIndex
          const buffer = this.lazyRange * this.slidesPerView
          return idx >= Math.max(0, startIndex - buffer) &&
            idx < Math.min(this.renderedSlides.length, startIndex + this.slidesPerView + buffer)
        }
        return Math.abs(idx - this.currentIndex) <= this.lazyRange
      }

      // loop: renderedSlides = [c-last, s0..s(n-1), c-first]. 클론과 그에 대응하는
      // 실제 슬라이드는 원형으로 인접하므로, idx의 등가 위치들도 함께 판정해야
      // 마지막<->첫번째로 순간이동하는 순간 반대편 실제 슬라이드가 뒤늦게 마운트되며
      // 깜빡이는 현상이 없다.
      const len = this.renderedSlides.length
      const active = this.actualIndex
      const equivalents = [idx]
      if (idx === 0) {
        equivalents.push(len - 2)
      } else if (idx === len - 1) {
        equivalents.push(1)
      } else if (idx === 1) {
        equivalents.push(len - 1)
      } else if (idx === len - 2) {
        equivalents.push(0)
      }

      return equivalents.some(pos => Math.abs(pos - active) <= this.lazyRange)
    }
  }
}
